import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../Contexts/AuthContext";
import { useCart } from "../../../Contexts/CartContext";
import { useTenant } from "../../../Contexts/TenantContext";
import { productService } from "../../../Api/Services/productService";
import { shippingService } from "../../../Api/Services/shippingService";
import { GetProduct } from "../../../Models/Product";
import { ShippingOptionResponse } from "../../../Models/Shipping";
import { CommercialUnit } from "../../../Enums/CommercialUnit";
import { formatPostalCode, formatPriceBRL, unformat } from "../../../Utils/DataFormatters";
import { listInstallments } from "../../../Utils/efiBankPayment";
import { Input } from "../../../Components/Input/Input";
import { FaFilePdf, FaImage, FaTruck, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import ProductImageGallery from "../../../Components/ProductImageGallery/ProductImageGallery";
import QuantitySelector from "../../../Components/QuantitySelector/QuantitySelector";
import Footer from "../../../Components/Footer/Footer";
import PaymentOptionsModal from "../../../Components/PaymentOptionsModal/PaymentOptionsModal";
import ShippingAndDeliveryModal from "../../../Components/ShippingAndDeliveryModal/ShippingAndDeliveryModal";
import EfiPay from "payment-token-efi";
import Button from "../../../Components/Button/Button";


const UserProductDetailsPage = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const { tenantPreference, tenantContact } = useTenant();
  const { user } = useAuth();
  const { cartItems, addToCart, removeFromCart } = useCart();

  const hideProductCode = tenantPreference?.autoGenerateProductCode === true;
  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;
  const enableBrandsFeature = tenantPreference?.enableBrandsFeature ?? true;
  const enableModelsFeature = tenantPreference?.enableModelsFeature ?? true;
  const enableMadeToOrderProducts = tenantPreference?.enableMadeToOrderProducts ?? true;
  
  const [product, setProduct] = useState<GetProduct | null>(null);
  const [productQuantity, setProductQuantity] = useState<number>(1);
  const [componentQuantities, setComponentQuantities] = useState<Record<string, number>>({});
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const SIZES_STANDARD = ["P", "M", "G", "GG"];
  const SIZES_PLUS = ["G1", "G2", "G3"];
  const ALL_SIZES = [...SIZES_STANDARD, ...SIZES_PLUS];

  const [cep, setCep] = useState<string | null>(null);
  const [shippingOptions, setShippingOptions] = useState<ShippingOptionResponse[] | null>(null);

  const [installments, setInstallments] = useState<EfiPay.CreditCard.Installment[]>([]);
  const [isFetchingInstallments, setIsFetchingInstallments] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);

  const imagesArray: string[] | null = [
    product?.productImageOne?.url,
    product?.productImageTwo?.url,
    product?.productImageThree?.url,
    product?.productImageFour?.url,
  ].filter((url): url is string => !!url);


  const resolveFileKind = (url?: string, blobName?: string) => {
    const source = `${url ?? ""} ${blobName ?? ""}`.toLowerCase();
    if (source.includes(".pdf")) {
      return "pdf";
    }

    if (/\.(jpg|jpeg|png|gif|webp)\b/.test(source)) {
      return "image";
    }

    return "file";
  };

  const documentItems = [
    product?.productDocumentOne
      ? { file: product.productDocumentOne, title: "Ficha Técnica / Desenho Técnico" }
      : null,
    product?.productDocumentTwo
      ? { file: product.productDocumentTwo, title: "Termo de Garantia" }
      : null
  ].filter((item): item is { file: NonNullable<GetProduct["productDocumentOne"]>; title: string } => Boolean(item));

  const loadProduct = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const product = await productService.get(id);
      if (!product) {
        setProduct(null);
        toast.info("O produto não foi encontrado!");
        return;
      }
      setProduct(product);
    } catch {
      setProduct(null);
      toast.error("Não foi possível carregar o produto.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      void loadProduct(id);
    }
  }, [id, loadProduct]);

  useEffect(() => {
    if (!product) {
      return;
    }

    // Sync product quantity from cart
    const cartProduct = cartItems.find(i => i.id === product.id);
    setProductQuantity(cartProduct ? cartProduct.quantity : 1);

    // Sync component quantities from cart
    const next: Record<string, number> = {};

    if (enableProductComponents) {
      product.components?.forEach(c => {
        const cid = String(c.componentId);
        const cartComp = cartItems.find(i => i.id === cid);
        next[cid] = cartComp ? cartComp.quantity : 1;
      });
    }

    setComponentQuantities(next);
  }, [product, cartItems, enableProductComponents]);

  useEffect(() => {
    const fetchVisaInstallments = async () => {
      if (!product?.price) {
        return;
      }

      const isPlusSize = !enableProductComponents && selectedSize !== null && SIZES_PLUS.includes(selectedSize);
      const effectivePrice = isPlusSize && product.plusPrice != null ? product.plusPrice : product.price;

      setIsFetchingInstallments(true);
      try {
        const totalCents = Math.round(effectivePrice * 100);
        const efiInstallments = await listInstallments("visa", totalCents);

        if (efiInstallments) {
          setInstallments(efiInstallments.installments);
        } else {
          setInstallments([]);
        }
      } catch {
        toast.error("Erro ao buscar opções de parcelamento para cartão de crédito.");
        setInstallments([]);
      } finally {
        setIsFetchingInstallments(false);
      }
    };

    fetchVisaInstallments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.price, product?.plusPrice, selectedSize, enableProductComponents]);

  const handleBuy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!enableProductComponents && !selectedSize) {
      toast.warn("Selecione um tamanho antes de adicionar ao carrinho.");
      return;
    }
    const effectiveIsMadeToOrder = enableMadeToOrderProducts && !!product?.isMadeToOrder;
    const isPlusSize = selectedSize !== null && SIZES_PLUS.includes(selectedSize);
    const effectivePrice = !enableProductComponents && isPlusSize && product!.plusPrice != null
      ? product!.plusPrice
      : product!.price;
    addToCart({
      id: product!.id,
      name: product!.name,
      code: product!.code,
      price: effectivePrice,
      image: product!.productImageOne?.url,
      stockQuantity: product!.stockQuantity,
      origin: product!.origin,
      isMadeToOrder: effectiveIsMadeToOrder,
      quantity: productQuantity,
      size: selectedSize ?? undefined,
      plusPrice: product!.plusPrice ?? undefined
    });
  };

  const buildShareOnWhatsAppLink = () => {
    if (!product) {
      return "#";
    }

    const encodedMessage = encodeURIComponent(
      `${"Confira este produto"}: ${product.name}\n${window.location.href}`
    );

    return `https://wa.me/?text=${encodedMessage}`;
  };

  const whatsAppPhone = unformat(tenantContact?.whatsAppNumber || "");
  const hasWhatsApp = whatsAppPhone.length > 0;
  const effectiveIsMadeToOrder = enableMadeToOrderProducts && !!product?.isMadeToOrder;

  const showWhatsAppConsultPrice =
    tenantPreference?.whatsAppConsultPriceEnabled === true &&
    !!product &&
    product.price === 0 &&
    hasWhatsApp;

  const showWhatsAppConsultStock =
    enableMadeToOrderProducts &&
    tenantPreference?.whatsAppConsultStockEnabled === true &&
    !!product &&
    effectiveIsMadeToOrder &&
    !(product.stockQuantity && product.stockQuantity > 0) &&
    hasWhatsApp;

  const buildWhatsAppConsultLink = (type: "price" | "stock") => {
    const msg =
      type === "price"
        ? `Olá, gostaria de saber o preço do produto: ${product?.name}`
        : `Olá, gostaria de saber o prazo de entrega do produto: ${product?.name}`;
    return `https://wa.me/${whatsAppPhone}?text=${encodeURIComponent(msg)}`;
  };

  const handleCalculateShipping = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!cep?.trim()) {
      toast.error("Você precisa informar o CEP de destino para o cálculo de frete e prazo.");
      return;
    }

    if (!product?.id) {
      toast.error("Produto inválido para cálculo de frete.");
      return;
    }

    setIsCalculatingShipping(true);
    try {
      const shippingOptions = await shippingService.getOptions({
        products: [
          { productId: product.id, quantity: productQuantity }
        ],
        destinationZip: cep
      });

      if (!shippingOptions) {
        toast.error("Não foi possível carregar as opções de frete e prazo dos Correios.");
        return;
      }

      setShippingOptions(shippingOptions);
      setIsShippingModalOpen(true);
    } catch (err) {
      toast.error("Não foi possível carregar as opções de frete e prazo dos Correios.");
    } finally {
      setIsCalculatingShipping(false);
    }
  };

  const handleNotifyMe = async (e: React.MouseEvent, productId: string) => {
    e.stopPropagation();

    if (!user) {
      toast.info("Acesse sua conta para ser avisado quando o produto estiver disponível.");
      return;
    }

    try {
      await productService.notifyMe(productId);
      toast.success("Te avisaremos quando o produto estiver disponível novamente.");
    } catch (err) {
      toast.error("Erro ao registrar interesse.");
    }
  };

  const isInCart = (pid: string, sz?: string | null) =>
    cartItems.some(i => i.id === pid && (sz == null || i.size === sz));

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="max-w-[74rem] mx-auto my-4 page-gutter py-4 sm:py-6 flex flex-col gap-5 sm:gap-10 sm:my-16">
      <div className="flex gap-5 sm:gap-10 flex-col sm:flex-row">
        <div className="sm:w-1/2 space-y-3">
          <SkeletonBlock height={320} />
          <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <SkeletonBlock key={idx} height={60} />
            ))}
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <SkeletonBlock height={32} width={260} />
          <SkeletonBlock height={20} width={180} />
          <SkeletonBlock height={18} width={160} />
          <SkeletonBlock height={18} width={140} />
          <SkeletonBlock height={24} width={200} />
          <SkeletonBlock height={18} width={240} />
          <SkeletonBlock height={50} />
          <SkeletonBlock height={44} />
          <SkeletonBlock height={44} />
          <SkeletonBlock height={44} width={200} />
          <SkeletonBlock height={44} width={220} />
        </div>
      </div>

      <div className="border-t pt-8 sm:mt-10 space-y-3">
        <SkeletonBlock height={24} width={200} />
        <SkeletonBlock height={14} />
        <SkeletonBlock height={14} />
        <SkeletonBlock height={14} width="80%" />
      </div>

      <div className="space-y-3">
        <SkeletonBlock height={24} width={200} />
        <SkeletonBlock height={120} />
      </div>

      <div className="space-y-3">
        <SkeletonBlock height={24} width={200} />
        <SkeletonBlock height={160} />
      </div>
    </div>
  );

  if (!product) {
    return (
      <div>
        <SmartNavbar />
        {isLoading ? (
          renderSkeleton()
        ) : (
          <div className="mx-auto max-w-6xl p-6">
            <p>{"O produto não foi encontrado."}</p>
          </div>
        )}
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <SmartNavbar />
      {isLoading ? (
        renderSkeleton()
      ) : (
        <div className={`max-w-[74rem] my-4 page-gutter py-4 sm:py-6 sm:my-16 mx-auto flex flex-col gap-5 sm:gap-10`}>
          <div className="flex gap-5 sm:gap-10 flex-col sm:flex-row items-start">
            <div className="w-full sm:w-1/2 flex flex-col gap-4">
              <ProductImageGallery images={imagesArray.length > 0 ? imagesArray : null} />
            </div>    
            <div className="flex-1 self-stretch">
              <div className={`bg-gray-100 p-4 rounded-md border h-full sm:min-h-[420px] flex flex-col`}>
                <h1 className={`text-xl font-semibold mb-1 sm:text-3xl `}>
                  {product?.name}
                </h1>
                {!hideProductCode && (
                  <h3 className="text-sm font-semibold text-gray-700 sm:text-base">
                    {"CÓD."} {product?.code}
                  </h3>
                )}
                {enableBrandsFeature && (
                <h3
                  className={`
                    text-sm font-medium text-gray-500 sm:text-base
                    ${product?.model || (product?.unit === CommercialUnit.CX && product?.unitsPerBoxText) ? "mb-0" : "mb-4"}
                  `}
                >
                  {"Marca"}: {product?.brand?.name}
                </h3>
                )}
                {enableModelsFeature && product?.model && (
                  <h3
                    className={`
                      text-sm font-medium text-gray-500 sm:text-base
                      ${product?.unit === CommercialUnit.CX && product?.unitsPerBoxText ? "mb-0" : "mb-4"}
                    `}
                  >
                    {"Modelo"}: {product?.model.name}
                  </h3>
                )}

                {product?.unit === CommercialUnit.CX && product?.unitsPerBoxText?.trim() && (
                  <h3 className="text-sm font-medium text-gray-500 mb-4 sm:text-base">
                    {product.unitsPerBoxText}
                  </h3>
                )}

                {(!effectiveIsMadeToOrder && !(product?.stockQuantity && product.stockQuantity > 0)) ? (
                  <div className="flex-1 flex flex-col items-center justify-center border border-gray-300 text-center py-3 px-4 rounded text-sm font-medium bg-white">
                    <p>{"Produto indisponível no momento."}</p>
                    <Button
                      className="mt-2 px-4 py-2"
                      onClick={(e) => handleNotifyMe(e, product!.id)}
                    >
                      {"Avise-me quando chegar"}
                    </Button>
                  </div>
                ) : showWhatsAppConsultPrice ? (
                  <div className="flex-1 flex flex-col items-center justify-center border border-green-200 text-center py-3 px-4 rounded text-sm font-medium bg-green-50">
                    <p className="text-gray-600 mb-3">
                      {"Entre em contato para consultar o preço deste produto."}
                    </p>
                    <a
                      href={buildWhatsAppConsultLink("price")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded font-semibold hover:bg-green-600 transition"
                    >
                      <FaWhatsapp />
                      {"Consultar preço"}
                    </a>
                  </div>
                ) : (
                  <div>
                    {effectiveIsMadeToOrder && !(product.stockQuantity && product.stockQuantity > 0) && !showWhatsAppConsultStock && (
                      <div className="border border-gray-300 text-center py-3 px-4 rounded text-xs font-medium mb-4 bg-white sm:text-sm">
                        <span>
                          {"Produto sob encomenda. Após criar o pedido, aguarde a disponibilidade para prosseguir com o pagamento."}
                        </span>
                      </div>
                    )}
                    {showWhatsAppConsultStock && (
                      <div className="border border-green-200 text-center py-3 px-4 rounded text-xs font-medium mb-4 bg-green-50 sm:text-sm">
                        <span>
                          {"Produto sob encomenda. Entre em contato para verificar prazo de entrega."}
                        </span>
                      </div>
                    )}
                    {product?.originalPrice && product?.discountPercentage && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 line-through">
                          {formatPriceBRL(product?.originalPrice)}
                        </span>
                        <span className="text-sm text-primary-600 font-medium">
                          -{product?.discountPercentage?.toFixed(0)}%
                        </span>
                      </div>
                    )}
                    <div className="text-xl font-bold text-primary-600 mb-1 sm:text-2xl sm:mb-2">
                      {!enableProductComponents && selectedSize && SIZES_PLUS.includes(selectedSize) && product?.plusPrice != null
                        ? formatPriceBRL(product.plusPrice)
                        : formatPriceBRL(product?.price || 0)}
                    </div>
                    <div className="text-sm text-gray-600 mb-2 sm:mb-4">
                      {(() => {
                        const maxNoInterest = tenantPreference?.offerCardNoInterest ? (tenantPreference.cardMaxNoInterestInstallments ?? 0) : 0;
                        const surcharge = (tenantPreference?.enableCreditCardSurcharge && (tenantPreference.creditCardSurchargePercent ?? 0) > 0)
                          ? tenantPreference.creditCardSurchargePercent / 100
                          : 0;
                        if (maxNoInterest > 1) {
                          const basePrice = product?.paymentOptions.pixPrice || product?.paymentOptions.bankingBilletPrice || product?.price || 0;
                          const cardPrice = basePrice * (1 + surcharge);
                          return <>{"ou até"} {maxNoInterest}x {"de"} {formatPriceBRL(cardPrice / maxNoInterest)} {"sem juros"}</>;
                        }
                        const totalInstallments = installments.length > 0 ? installments.length : product?.paymentOptions.creditCardInstallments.length ?? 0;
                        const lastValue = (() => {
                          if (installments.length === 0) {
                            return (product?.paymentOptions.creditCardInstallments.slice(-1)[0]?.installmentPrice ?? 0) * (1 + surcharge);
                          }
                          return (installments[installments.length - 1].value / 100) * (1 + surcharge);
                        })();
                        return <>{"ou até"} {totalInstallments}x {"de"} {formatPriceBRL(lastValue)} {"com juros"}</>;
                      })()}
                    </div>
                    {!enableProductComponents && (
                      <div className="my-3">
                        <p className="text-sm font-medium mb-2">{"Tamanho"}:</p>
                        <div className="flex flex-wrap gap-2">
                          {(product?.availableSizes?.length ? product.availableSizes : ALL_SIZES).map(sz => {
                            const szInCart = isInCart(product!.id, sz);
                            return (
                              <button
                                key={sz}
                                onClick={() => setSelectedSize(sz)}
                                className={`px-3 py-1 rounded border text-sm font-semibold transition-colors relative
                                  ${selectedSize === sz
                                    ? "bg-primary-600 text-white border-primary-600"
                                    : szInCart
                                      ? "bg-green-100 text-green-700 border-green-400"
                                      : "bg-white text-gray-700 border-gray-300 hover:border-primary-500"}
                                `}
                              >
                                {sz}
                                {szInCart && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-green-500" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="p-2 border rounded bg-gray-50 flex items-center gap-2 my-2">
                      <span className="font-medium">{"Quantidade"}:</span>
                      <QuantitySelector
                        value={productQuantity}
                        min={1}
                        max={(product?.stockQuantity ?? 0) > 0 ? (product?.stockQuantity ?? 0) : 300}
                        onChange={setProductQuantity}
                        align="left"
                        disabled={!!product && isInCart(product.id, selectedSize)}
                      />
                    </div>
                    {!showWhatsAppConsultStock && (
                    <div className="text-center pt-5 pb-7">
                      <button
                        className="text-sm hover:underline"
                        onClick={() => setIsPaymentModalOpen(true)}
                      >
                        {"Ver todas as formas de pagamento"}
                      </button>
                    </div>
                    )}
                    {showWhatsAppConsultStock ? (
                      <a
                        href={buildWhatsAppConsultLink("stock")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold py-3 px-6 rounded hover:bg-green-600 transition"
                      >
                        <FaWhatsapp />
                        {"CONSULTAR ESTOQUE"}
                      </a>
                    ) : (!enableProductComponents && !selectedSize ? false : isInCart(product.id, !enableProductComponents ? selectedSize : null)) ? (
                      <Button variant="muted"
                        className="w-full py-3 px-6"
                        onClick={(e) => {
                          e.preventDefault();
                          const key = !enableProductComponents && selectedSize
                            ? `${product.id}:${selectedSize}`
                            : product.id;
                          removeFromCart(key);
                          setProductQuantity(1);
                          setSelectedSize(null);
                        }}
                      >
                        {"REMOVER DO CARRINHO"}
                      </Button>
                    ) : (
                      <Button
                        className="w-full py-3 px-6"
                        onClick={handleBuy}
                      >
                        {effectiveIsMadeToOrder && !(product.stockQuantity && product.stockQuantity > 0)
                          ? "ENCOMENDAR"
                          : "COMPRAR"}
                      </Button>
                    )}

                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/10 text-green-600">
                        <FaWhatsapp />
                      </div>
                      <a
                        href={buildShareOnWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-green-600 hover:underline font-medium"
                      >
                        {"Compartilhar produto"}
                      </a>
                    </div>
                    <div className="mt-3 gap-2 flex flex-col md:flex-row items-start md:items-center">
                      <p className="text-sm sm:text-md whitespace-nowrap">{"Frete e prazo"}</p>
                      
                      <div className="flex gap-2 flex-1">
                        <Input
                          className="flex-1"
                          placeholder={"Insira seu CEP"}
                          type="text"
                          name="cep"
                          value={formatPostalCode(cep || "")}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            setCep(raw.slice(0, 8));
                          }}
                        />

                        <Button isLoading={isCalculatingShipping} icon={<FaTruck className="text-xs shrink-0" />}
                          onClick={handleCalculateShipping}
                          className="px-4 py-3 min-w-[8.5rem]"
                          disabled={isCalculatingShipping}
                        >
                          {"CALCULAR"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`border-t pt-8 sm:mt-10`}>
            {product?.description && (
              <>
                <h2 className={`text-xl font-bold mb-2 sm:mb-4 `}>{"Descrição do Produto"}</h2>
                <p className={`mb-4 sm:mb-6 text-gray-700 whitespace-pre-wrap`}>{product.description}</p>
              </>
            )}

            {(product?.specifications?.length ?? 0) > 0 && (
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <tbody>
                  {product?.specifications?.map((specification, idx) => (
                    <tr key={idx}>
                      <td className="border p-3 font-semibold">{specification.key}</td>
                      <td className="border p-3">{specification.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {documentItems.length > 0 && (
            <div className="mt-4">
              <h2 className="text-xl font-bold mb-2 sm:mb-4">{"Documentos"}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documentItems.map(({ file, title }) => {
                  const fileKind = resolveFileKind(file.url, file.blobName);

                  return (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-4 border rounded-md hover:bg-gray-50"
                  >
                    {fileKind === "pdf" ? (
                      <FaFilePdf className="text-primary-600 text-xl" />
                    ) : (
                      <FaImage className="text-primary-600 text-xl" />
                    )}
                    <span>{title}</span>
                  </a>
                  );
                })}
              </div>
            </div>
          )}

          {enableProductComponents && product?.components && product.components.length > 0 && (
            <div className="mt-4">
              <h2 className="text-xl font-bold mb-2 sm:mb-4">{"Peças"}</h2>
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-primary-120">
                    <th className="border p-3 text-center hidden sm:table-cell">{"Imagem"}</th>
                    <th className="border p-3 text-center hidden sm:table-cell">{"Código"}</th>
                    <th className="border p-3 text-left">{"Nome"}</th>
                    <th className="border p-3 text-center">{"Preço"}</th>
                    <th className="border p-3 text-center hidden sm:table-cell">{"Quantidade"}</th>
                    <th className="border p-3 text-center">{"Ações"}</th>
                  </tr>
                </thead>
                <tbody>
                  {product.components.map((c, idx) => {
                    const comp = c.component;
                    return (
                      <tr
                        key={idx}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/produtos/${c.componentId}`)}
                      >
                        <td className="border p-3 text-center hidden sm:table-cell">
                          <div className="w-12 h-12 flex items-center justify-center mx-auto">
                            {comp.mainImageUrl ? (
                              <img
                                src={comp.mainImageUrl}
                                alt={comp.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-400 rounded text-xs">
                                {"Sem imagem"}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="border p-3 text-center hidden sm:table-cell">{comp.code}</td>
                        <td className="border p-3">{comp.name}</td>
                        <td className="border p-3 text-center">{formatPriceBRL(comp.price)}</td>
                        <td className="border p-3 text-center hidden sm:table-cell">
                          {comp.stockQuantity > 0 ? (
                            <QuantitySelector
                              value={componentQuantities[String(comp.id)] || 1}
                              min={1}
                              max={comp.stockQuantity}
                              onChange={(val) =>
                                setComponentQuantities(prev => ({ ...prev, [String(comp.id)]: val }))
                              }
                              disabled={isInCart(comp.id)}
                            />
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                        <td className="border p-3 text-center">
                          {comp.stockQuantity <= 0 ? (
                            <Button variant="muted"
                              className="text-sm px-3 py-1 min-w-28 inline-block text-center"
                              onClick={(e) => handleNotifyMe(e, comp.id)}
                            >
                              {"Avise-me"}
                            </Button>
                          ) : (
                            isInCart(comp.id) ? (
                              <Button variant="muted"
                                className="px-3 py-1 text-sm min-w-28"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromCart(comp.id);
                                  setComponentQuantities(prev => ({ ...prev, [String(comp.id)]: 1 }));
                                }}
                              >
                                {"Remover"}
                              </Button>
                            ) : (
                              <Button
                                className="px-3 py-1 text-sm min-w-28"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart({
                                    id: comp.id,
                                    name: comp.name,
                                    code: comp.code,
                                    price: comp.price,
                                    image: comp.mainImageUrl,
                                    stockQuantity: comp.stockQuantity,
                                    origin: comp.origin,
                                    isMadeToOrder: comp.isMadeToOrder,
                                    quantity: componentQuantities[String(comp.id)] || 1
                                  });
                                }}
                              >
                                {"Comprar"}
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <Footer />

      <PaymentOptionsModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        bankingBilletPrice={product?.paymentOptions.bankingBilletPrice!}
        pixPrice={product?.paymentOptions.pixPrice!}
        creditCardInstallments={installments}
        isFetchingInstallments={isFetchingInstallments}
        quantity={productQuantity}
        maxNoInterestInstallments={
          tenantPreference?.offerCardNoInterest
            ? tenantPreference.cardMaxNoInterestInstallments
            : 0
        }
        enableBankingBillet={tenantPreference?.enableBankingBillet ?? true}
        enablePix={tenantPreference?.enablePix ?? true}
        enableCreditCard={tenantPreference?.enableCreditCard ?? true}
        creditCardSurchargePercent={
          tenantPreference?.enableCreditCardSurcharge
            ? (tenantPreference.creditCardSurchargePercent ?? 0)
            : 0
        }
      />
      <ShippingAndDeliveryModal
        isOpen={isShippingModalOpen}
        onClose={() => setIsShippingModalOpen(false)}
        shippingOptions={shippingOptions}
      />
    </div>
  );
};

export default UserProductDetailsPage;
